import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { CheckCircle2, History, Loader2, Pencil, Repeat, RotateCcw, Trash2, X } from 'lucide-react';
import { Modal, ConfirmDialog } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { PermissionGuard } from '@components/PermissionGuard';
import { usePermissions } from '@hooks/usePermissions';
import activityService from '@services/activityService';
import boardService from '@services/boardService';
import type { Activity, ActivityFormData, ActivityHistory, Priority } from '@/types/activity';
import type { Board } from '@/types/board';
import { BOARD_TYPE_LABELS } from '@/types/board';
import { PRIORITIES, PRIORITY_LABELS } from '@/utils/priority';
import { formatDateBR, formatDateTimeBR } from '@/utils/formatters';
import { PriorityBadge } from './ActivityBits';

/** Valores iniciais de uma atividade nova (ex.: "+" de uma coluna, dia do calendário). */
export interface ActivityPreset {
  board_id?: number;
  board_column_id?: number;
  due_date?: string;
  start_date?: string;
  start_time?: string;
  priority?: Priority;
}

interface ActivityModalProps {
  activityId?: number;
  preset?: ActivityPreset;
  onClose: () => void;
  onChanged: () => void;
}

interface FormState {
  board_id: number | '';
  board_column_id: number | '';
  title: string;
  description: string;
  priority: Priority;
  start_date: string;
  due_date: string;
  start_time: string;
  end_time: string;
}

const inputCls = 'input-field';
const labelCls = 'text-label block mb-1';

/**
 * Janela única de criar/editar atividade (usada em Hoje, kanban, calendário, lista e sino).
 * Prazo é opcional: atividade "sem prazo" fica pendente de priorização. Hora de início/fim é
 * opcional e serve para a agenda do dia.
 */
export const ActivityModal: React.FC<ActivityModalProps> = ({ activityId, preset, onClose, onChanged }) => {
  const isEdit = activityId !== undefined;
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('agenda.activities.write').hasPermission;

  const [tab, setTab] = useState<'details' | 'history'>('details');
  const [activity, setActivity] = useState<Activity | null>(null);
  const [boards, setBoards] = useState<Board[]>([]);
  const [board, setBoard] = useState<Board | null>(null);
  const [history, setHistory] = useState<ActivityHistory[] | null>(null);
  const [form, setForm] = useState<FormState>({
    board_id: preset?.board_id ?? '',
    board_column_id: preset?.board_column_id ?? '',
    title: '',
    description: '',
    priority: preset?.priority ?? 'medium',
    start_date: preset?.start_date ?? '',
    due_date: preset?.due_date ?? '',
    start_time: preset?.start_time ?? '',
    end_time: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Carga inicial: quadros do usuário + a atividade (edição).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [boardList, act] = await Promise.all([
          boardService.getAll(),
          activityId !== undefined ? activityService.getById(activityId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setBoards(boardList);
        if (act) {
          setActivity(act);
          setForm({
            board_id: act.board_id,
            board_column_id: act.board_column_id,
            title: act.title,
            description: act.description ?? '',
            priority: act.priority,
            start_date: act.start_date ?? '',
            due_date: act.due_date ?? '',
            start_time: act.start_time ?? '',
            end_time: act.end_time ?? '',
          });
        } else if (!preset?.board_id) {
          const main = boardList.find((b) => b.is_main) ?? boardList[0];
          if (main) setForm((f) => ({ ...f, board_id: main.id }));
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId]);

  // Colunas do quadro escolhido.
  useEffect(() => {
    if (!form.board_id) {
      setBoard(null);
      return;
    }
    let cancelled = false;
    boardService
      .getById(Number(form.board_id))
      .then((b) => {
        if (!cancelled) setBoard(b);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [form.board_id]);

  const loadHistory = useCallback(async () => {
    if (activityId === undefined) return;
    try {
      setHistory(await activityService.getHistory(activityId));
    } catch (err) {
      setError(err);
      setHistory([]);
    }
  }, [activityId]);

  useEffect(() => {
    if (tab === 'history' && history === null) loadHistory();
  }, [tab, history, loadHistory]);

  const columns = board?.columns ?? [];
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const changeBoard = (value: string) => {
    setForm((f) => ({ ...f, board_id: value ? Number(value) : '', board_column_id: '' }));
  };

  const dateError =
    form.start_date && form.due_date && form.start_date > form.due_date
      ? 'O início não pode ser depois do prazo.'
      : null;
  const timeError =
    form.start_time && form.end_time && (!form.start_date || !form.due_date || form.start_date === form.due_date) && form.end_time < form.start_time
      ? 'A hora de fim não pode ser antes da hora de início.'
      : null;

  const canSubmit = canWrite && !!form.board_id && form.title.trim().length > 0 && !dateError && !timeError && !saving;

  const buildPayload = (): ActivityFormData => ({
    board_id: Number(form.board_id),
    board_column_id: form.board_column_id ? Number(form.board_column_id) : null,
    title: form.title.trim(),
    description: form.description.trim() ? form.description.trim() : null,
    priority: form.priority,
    start_date: form.start_date || null,
    due_date: form.due_date || null,
    start_time: form.start_time || null,
    end_time: form.end_time || null,
  });

  const run = async (action: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await action();
      onChanged();
      onClose();
    } catch (err) {
      setConfirmDelete(false);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!canSubmit) return;
    run(() => (activityId !== undefined ? activityService.update(activityId, buildPayload()) : activityService.create(buildPayload())));
  };
  const handleComplete = () => activityId !== undefined && run(() => activityService.complete(activityId));
  const handleReopen = () => activityId !== undefined && run(() => activityService.reopen(activityId));
  const handleDelete = () => activityId !== undefined && run(() => activityService.delete(activityId));

  const handleMakeRecurring = () => {
    const params = new URLSearchParams({ new: '1' });
    if (form.title.trim()) params.set('title', form.title.trim());
    if (form.description.trim()) params.set('description', form.description.trim());
    if (form.board_id) params.set('board_id', String(form.board_id));
    if (form.board_column_id) params.set('board_column_id', String(form.board_column_id));
    params.set('priority', form.priority);
    const start = form.due_date || form.start_date;
    if (start) params.set('start_date', start);
    onClose();
    navigate(`/recurrences?${params.toString()}`);
  };

  const dailyBoards = boards.filter((b) => b.board_type === 'daily');
  const projectBoards = boards.filter((b) => b.board_type === 'project');
  // O quadro atual pode não vir na listagem (ex.: arquivado) — garante a opção.
  const boardMissing = !!form.board_id && !boards.some((b) => b.id === form.board_id);

  return (
    <>
      <Modal isOpen onClose={onClose} size="xl" showCloseButton={false} closeOnBackdrop={!saving}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2 min-w-0">
            {isEdit ? <Pencil size={18} className="text-slate-500" /> : null}
            <h2 className="text-h3 truncate">{isEdit ? 'Atividade' : 'Nova atividade'}</h2>
            {activity?.is_done && <span className="badge-success">Concluída</span>}
            {activity && !activity.is_done && activity.is_overdue && <span className="badge-danger">Atrasada</span>}
            {activity && <PriorityBadge priority={activity.priority} />}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        {isEdit && (
          <div className="px-6 border-b border-slate-200 flex gap-6">
            {(['details', 'history'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={clsx(
                  'py-3 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
                  tab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700',
                )}
              >
                {t === 'details' ? <Pencil size={14} /> : <History size={14} />}
                {t === 'details' ? 'Detalhes' : 'Histórico'}
              </button>
            ))}
          </div>
        )}

        <div className="px-6 py-4 max-h-[65vh] overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 size={20} className="animate-spin mr-2" /> Carregando…
            </div>
          ) : tab === 'history' ? (
            <HistoryList history={history} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>
                  Quadro <span className="text-red-500">*</span>
                </label>
                <select
                  className="select-field"
                  value={form.board_id}
                  onChange={(e) => changeBoard(e.target.value)}
                  disabled={!canWrite}
                >
                  <option value="">Selecione…</option>
                  {boardMissing && <option value={form.board_id}>{activity?.board_name ?? `Quadro #${form.board_id}`}</option>}
                  {dailyBoards.length > 0 && (
                    <optgroup label={BOARD_TYPE_LABELS.daily}>
                      {dailyBoards.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                          {b.is_main ? ' (principal)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {projectBoards.length > 0 && (
                    <optgroup label="Projetos">
                      {projectBoards.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div>
                <label className={labelCls}>Coluna</label>
                <select
                  className="select-field"
                  value={form.board_column_id}
                  onChange={(e) => set('board_column_id', e.target.value ? Number(e.target.value) : '')}
                  disabled={!canWrite || !board}
                >
                  <option value="">{isEdit ? 'Manter' : 'Primeira coluna aberta'}</option>
                  {columns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.is_done_column ? ' (concluído)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={labelCls}>
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  className={inputCls}
                  value={form.title}
                  maxLength={300}
                  onChange={(e) => set('title', e.target.value)}
                  disabled={!canWrite}
                  autoFocus={!isEdit}
                  placeholder="O que precisa ser feito?"
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelCls}>Descrição</label>
                <textarea
                  className="textarea-field"
                  rows={4}
                  maxLength={8000}
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  disabled={!canWrite}
                  placeholder="Detalhes, links, referências…"
                />
              </div>

              <div>
                <label className={labelCls}>Prioridade</label>
                <select
                  className="select-field"
                  value={form.priority}
                  onChange={(e) => set('priority', e.target.value as Priority)}
                  disabled={!canWrite}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Início</label>
                <DateInputBR
                  className="input-field w-full"
                  value={form.start_date}
                  onChange={(v) => set('start_date', v)}
                  disabled={!canWrite}
                />
              </div>

              <div>
                <label className={labelCls}>Prazo</label>
                <div className="flex items-center gap-2">
                  <DateInputBR
                    className="input-field w-full"
                    value={form.due_date}
                    onChange={(v) => set('due_date', v)}
                    disabled={!canWrite}
                    placeholder="sem prazo"
                  />
                  {form.due_date && canWrite && (
                    <button
                      type="button"
                      onClick={() => set('due_date', '')}
                      className="text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap"
                      title="Deixar sem prazo (pendente de priorização)"
                    >
                      Sem prazo
                    </button>
                  )}
                </div>
                {!form.due_date && (
                  <p className="text-small mt-1">Sem prazo: fica em "Pendentes de priorização".</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Hora início</label>
                  {/* type="time" é permitido (a proibição é só para type="date") */}
                  <input
                    type="time"
                    className={inputCls}
                    value={form.start_time}
                    onChange={(e) => set('start_time', e.target.value)}
                    disabled={!canWrite}
                    step={300}
                  />
                </div>
                <div>
                  <label className={labelCls}>Hora fim</label>
                  <input
                    type="time"
                    className={inputCls}
                    value={form.end_time}
                    onChange={(e) => set('end_time', e.target.value)}
                    disabled={!canWrite}
                    step={300}
                  />
                </div>
                <p className="col-span-2 text-small -mt-2">Opcional. Aparece no calendário e nos cartões do Kanban.</p>
              </div>

              {dateError && <p className="md:col-span-2 text-sm text-red-600">{dateError}</p>}
              {timeError && <p className="md:col-span-2 text-sm text-red-600">{timeError}</p>}

              {activity && (
                <div className="md:col-span-2 text-small border-t border-slate-100 pt-3 flex flex-wrap gap-x-4 gap-y-1">
                  <span>Criada em {formatDateTimeBR(activity.created_at)}</span>
                  {activity.updated_at && <span>Alterada em {formatDateTimeBR(activity.updated_at)}</span>}
                  {activity.completed_at && <span>Concluída em {formatDateTimeBR(activity.completed_at)}</span>}
                  {activity.occurrence_date && (
                    <span className="inline-flex items-center gap-1">
                      <Repeat size={11} /> Ocorrência de {formatDateBR(activity.occurrence_date)}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {isEdit && activity && !activity.is_done && (
              <PermissionGuard permission="agenda.activities.write">
                <Button variant="success" size="sm" icon={<CheckCircle2 />} onClick={handleComplete} disabled={saving}>
                  Concluir
                </Button>
              </PermissionGuard>
            )}
            {isEdit && activity?.is_done && (
              <PermissionGuard permission="agenda.activities.write">
                <Button variant="outline" size="sm" icon={<RotateCcw />} onClick={handleReopen} disabled={saving}>
                  Reabrir
                </Button>
              </PermissionGuard>
            )}
            {isEdit && (
              <PermissionGuard permission="agenda.activities.write">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Trash2 />}
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Excluir
                </Button>
              </PermissionGuard>
            )}
            {!activity?.recurrence_rule_id && (
              <PermissionGuard permission="agenda.recurrences.manage">
                <button
                  type="button"
                  onClick={handleMakeRecurring}
                  disabled={!form.board_id || !form.title.trim()}
                  className="text-sm text-emerald-700 hover:text-emerald-800 hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:no-underline"
                >
                  <Repeat size={14} /> Tornar recorrente
                </button>
              </PermissionGuard>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              {canWrite ? 'Cancelar' : 'Fechar'}
            </Button>
            {canWrite && tab === 'details' && (
              <Button variant="primary" onClick={handleSave} loading={saving} disabled={!canSubmit}>
                {isEdit ? 'Salvar' : 'Criar atividade'}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Excluir atividade"
        message={`Excluir "${activity?.title ?? ''}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        severity="danger"
        loading={saving}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </>
  );
};

const HistoryList: React.FC<{ history: ActivityHistory[] | null }> = ({ history }) => {
  if (history === null) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 size={20} className="animate-spin mr-2" /> Carregando histórico…
      </div>
    );
  }
  if (history.length === 0) {
    return <p className="text-sm text-slate-400 text-center py-8">Nenhum evento registrado.</p>;
  }
  return (
    <ol className="relative border-l border-slate-200 ml-2 space-y-4">
      {history.map((h) => (
        <li key={h.id} className="ml-4">
          <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
          <p className="text-sm text-slate-800">{h.description}</p>
          {(h.old_value || h.new_value) && (
            <p className="text-xs text-slate-500 mt-0.5">
              {h.old_value && <span className="line-through mr-1">{h.old_value}</span>}
              {h.new_value && <span className="font-medium text-slate-700">{h.new_value}</span>}
            </p>
          )}
          <p className="text-[11px] text-slate-400 mt-0.5">{formatDateTimeBR(h.created_at)}</p>
        </li>
      ))}
    </ol>
  );
};
